<template>
  <OfferTile
    :offer="offer"
    :card="customCard"
    :tag-title="tagTitle"
    :link-tag="linkTag"
    :lazy="lazy"
    :no-text-brand="noTextBrand"
    :is-highlight="isHighlight"
    :highlight-content="highlightContent"
    :hide-variants="hideVariants"
    :hide-color-variants="hideColorVariants"
    :hide-size-variants="hideSizeVariants"
    :hide-storage-variants="hideStorageVariants"
    :hide-memory-variants="hideMemoryVariants"
    :hide-contract="hideContract"
    :hide-energy-label="hideEnergyLabel"
    :hide-app-icons="hideAppIcons"
    :limit-color-variants="limitColorVariants"
    :image-index="imageIndex"
    :hide-product-image="hideProductImage"
    :slug-prefix="slugPrefix"
    image-sizes="xs:180px"
  >
    <template #image>
      <div
        v-if="!imageSources"
        class="offer-tile__image-404"
      />
    </template>
    <template #brand>
      <CdnImage
        v-if="brand?.logo"
        :class="['offer-tile__logo-image', `offer-tile__logo-image--${brand.slug}`]"
        render-without-figure
        :src="brand.logo"
        :alt="`${brand.name} Logo`"
        :image-style="`aspect-ratio: ${brand.aspectRatio}`"
        :lazy="lazy"
      />
      <span
        v-else-if="brand"
        class="offer-tile__brand-placeholder"
        >{{ brand.name }}</span
      >
    </template>
    <template
      v-if="telekomContractLink && !hideContractLink"
      #offerContract
    >
      <LinkButton
        class="offer-tile__contract-link link--external"
        tag="button"
        variant="text"
        @click.prevent="clickContractLink"
      >
        {{ contractLinkText }}
        <span class="sr-only">(Der Link wird in einem neuen Tab geöffnet)</span>
      </LinkButton>
    </template>
    <!-- TODO: hide delivery time with props -->
    <template
      v-if="!hideDeliveryTime"
      #info
    >
      <slot name="info">
        <div
          v-if="inStock"
          class="offer-tile__delivery"
          aria-hidden="true"
        >
          <span v-if="availability?.deliveryTime">
            Lieferzeit: <span class="text text--nowrap">{{ availability.deliveryTime }}</span>
          </span>
        </div>
        <div
          v-else-if="availability"
          class="offer-tile__unavailable"
        >
          {{ availability.text }}
        </div>
      </slot>
    </template>
    <template #button>
      <LinkButton
        v-if="showGoToOfferButton"
        tag="span"
        class="offer-tile__button offer-tile__link"
        variant="secondary"
      >
        Zum Angebot
      </LinkButton>

      <AddToCartButtonContainer
        v-if="hasCartButton"
        class="offer-tile__button offer-tile__add-to-cart"
        display-cart-quantity
        :display-text="displayCartButtonText"
        :offer="offer"
      />

      <client-only>
        <LinkButton
          v-if="isBookmarkingAvailable && !hideBookmark"
          class="offer-tile__bookmark offer-tile__button"
          :class="{ 'offer-tile__bookmark--active': isBookmarked }"
          tag="button"
          :variant="isBookmarked ? 'outline-primary' : 'outline'"
          small-padding
          icon-only
          :aria-label="
            !isBookmarked
              ? 'Produkt zur Merkliste hinzufügen'
              : 'Produkt von der Merkliste entfernen'
          "
          @click.prevent="toggleBookmarkNumber"
        >
          <svg-icon
            no-icon-class
            class="offer-tile__bookmark-icon"
            :image="IconBookmark"
          />
        </LinkButton>
      </client-only>
    </template>
    <template
      v-if="!hideAppIcons && (isMzaCompatible || isMshCompatible)"
      #icon
    >
      <span
        v-if="isMzaCompatible"
        class="offer-tile__svg-icon-wrapper"
        title="MagentaZuhause App"
      >
        <SvgIcon
          :image="mzaIcon"
          class="offer-tile__mza-icon"
          aria-label="Kompatibel mit der MagentaZuhause App"
          :aria-hidden="false"
        />
      </span>
      <span
        v-if="isMshCompatible"
        class="offer-tile__svg-icon-wrapper"
        title="Magenta SmartHome App"
      >
        <SvgIcon
          :image="mshIcon"
          :aria-hidden="false"
          aria-label="Kompatibel mit der Magenta SmartHome App"
        />
      </span>
    </template>
  </OfferTile>
</template>

<script setup lang="ts">
import { merge, cloneDeep } from 'lodash-es';
import { useRoute } from '#imports';
import { storeToRefs } from 'pinia';
import { useOfferStore } from '~/stores/offers';
import { useCouponStore } from '~/stores/coupon';
import { OfferTile, LinkButton, calculateAvailability } from '@i22team/td-longtail-vue-components';
import { getDiscount, getPrice, getBundleBundledProducts } from '@i22team/rocket';

import CdnImage from '~/components/cdn-image.vue';
import AddToCartButtonContainer from '~/components/product-detail/add-to-cart-button-container.vue';

import { openModal } from '~/composables/modal';
import useProductBookmarks from '~/composables/product-bookmarks';

import { getBrand } from '~/lib/brand';
import { getMainImageFromOffer } from '~/lib/goliath-media';
import { getTelekomContractLink, getTelekomContractLinkText } from '~/lib/telekom-contract-link';

import IconBookmark from '~/assets/images/icon-bookmark.svg';

import mzaIcon from '~/assets/images/mza-logo-magenta-solid.svg';
import mshIcon from '~/assets/images/msh-logo.svg';

import type {
  CardComponentProps,
  SquareBadgeComponentProps,
} from '@i22team/td-longtail-vue-components';
import { tealiumLink } from '~/lib/tealium';
import type { ScopedOffer } from '@i22team/rocket';
import type { ListQuery, OfferQuery } from '#shared/types/goliath/goliath.types';

const HIGHLIGHTS_OFFER = [
  'DCS8302LH-SPK',
  '4251281645534',
  '8718699787059',
  '4251281641420',
  '4251281645138',
];

const $route = useRoute();

export type OfferTileContainerProps = {
  tagTitle?: string;
  offer: ListOffer | ScopedOffer<OfferQuery, ListQuery>;
  hasCartButton?: boolean;
  linkTag?: string;
  noTextBrand?: boolean;
  isHighlight?: boolean;
  highlightContent?: string;
  hideVariants?: boolean;
  hideColorVariants?: boolean;
  hideStorageVariants?: boolean;
  hideSizeVariants?: boolean;
  hideMemoryVariants?: boolean;
  hideContract?: boolean;
  hideEnergyLabel?: boolean;
  limitColorVariants?: number;
  card?: CardComponentProps | undefined;
  lazy?: boolean;
  hideDeliveryTime?: boolean;
  hideBookmark?: boolean;
  hideAppIcons?: boolean;
  showGoToOfferButton?: boolean;
  hideContractLink?: boolean;
  imageIndex?: number;
  slugPrefix?: string;
  imageSizes?: string;
  displayCartButtonText?: boolean;
  hideProductImage?: boolean;
};

const props = withDefaults(defineProps<OfferTileContainerProps>(), {
  tagTitle: 'h2',
  linkTag: 'nuxt-link',
  highlightContent: undefined,
  limitColorVariants: 3,
  card: undefined,
  lazy: false,
  imageIndex: 0,
  slugPrefix: undefined,
  imageSizes: undefined,
});

const {
  getCoupon,
  isValidCoupon,
  reductionValue,
  compatibleArticleNumbers,
  compatibleProductProviders,
  incompatibleArticleNumbers,
} = storeToRefs(useCouponStore());
const { zeroPercentFinancing, offerBadges } = storeToRefs(useOfferStore());

const { isProductInBookmarks, isBookmarkingAvailable, toggleBookmark } = useProductBookmarks();

const isBookmarked = computed(() => isProductInBookmarks(props.offer?.product?.articleNumber));

const availability = computed(() =>
  calculateAvailability(props.offer.product?.availability || undefined, true)
);

const productOfferBadges = computed(() =>
  offerBadges.value.filter((item) => item.articleNumber === props.offer?.product?.articleNumber)
);

const deliveryLeadTime = computed(() => props.offer.product?.deliveryLeadTime);

const imageSources = computed(() => getMainImageFromOffer(props.offer));

const brand = computed(() => getBrand(props.offer));

const productTags = computed(() => {
  if (!props.offer?.product) return [];
  const tags: string[] = [];

  const bundledProducts = getBundleBundledProducts(props.offer.product);

  if (bundledProducts && (bundledProducts?.length || 0) > 0) {
    bundledProducts[0]?.tags?.forEach((tag) => {
      if (tag && bundledProducts.every((product) => product?.tags?.includes(tag))) {
        tags.push(tag);
      }
    });
  } else if (props.offer.product.tags) {
    props.offer.product.tags.forEach((tag) => {
      if (tag) {
        tags.push(tag);
      }
    });
  }
  return tags?.map((str) => str.toLowerCase()) || [];
});

const isMzaCompatible = computed(() =>
  ['mzabasic', 'mzapro'].some((tag) => productTags.value.includes(tag))
);

const isMshCompatible = computed(() =>
  ['mshfree', 'mshpro'].some((tag) => productTags.value.includes(tag))
);

const inStock = computed(() => {
  const { product } = props.offer;
  if (!product) return true;
  return (product.availability?.stock || 0) > 0;
});

const price = computed(() => getPrice(props.offer.prices, 'DEFAULT', 'ONETIME')?.value);

const wasPrice = computed(() => getPrice(props.offer.prices, 'WAS', 'ONETIME')?.value);

const articleNumbersWithAlternatives = computed(() => {
  const articleNumbers = [props.offer?.product?.articleNumber];
  const productAlternativeSelectorGroup = props.offer.productAlternatives?.[0]?.properties || [];
  productAlternativeSelectorGroup.forEach((property) => {
    if (property?.alternatives) {
      articleNumbers.push(
        ...property.alternatives
          .map((alternative) => alternative?.offer?.product?.articleNumber)
          .filter((articleNumber) => articleNumber && !articleNumbers.includes(articleNumber))
      );
    }
  });

  return articleNumbers.filter(Boolean);
});

const hasHighlightFlag = computed(() =>
  articleNumbersWithAlternatives.value.some((articleNumber) =>
    HIGHLIGHTS_OFFER.includes(articleNumber)
  )
);

const discount = computed(() => getDiscount(price.value, wasPrice.value));

const telekomContractLink = computed(() =>
  props.offer.product?.availability?.provider === 'TELEKOM'
    ? getTelekomContractLink(props.offer)
    : ''
);

const contractLinkText = computed(() => getTelekomContractLinkText($route.path));

const isCouponCompatible = computed(() => {
  if (compatibleArticleNumbers.value?.length > 0) {
    return compatibleArticleNumbers.value.includes(props.offer.product?.articleNumber || '');
  }
  if (compatibleProductProviders.value?.length > 0) {
    return (
      props.offer.product?.availability?.provider &&
      compatibleProductProviders.value.includes(props.offer.product.availability.provider)
    );
  }
  if (incompatibleArticleNumbers.value?.length > 0) {
    return !incompatibleArticleNumbers.value.includes(props.offer.product?.articleNumber || '');
  }
  return true;
});

const hasZeroPercentFinancing = computed(() =>
  zeroPercentFinancing.value.includes(props.offer.product?.articleNumber || '')
);

const hasTreueshopFlag = computed(
  () =>
    isValidCoupon.value &&
    getCoupon.value?.type === 'coupon' &&
    reductionValue.value &&
    getCoupon.value?.typeCode === '02' &&
    isCouponCompatible.value &&
    !props.offer.prices?.some((p) => p?.type?.toLowerCase() === 'was')
);

const treueShopFlagText = computed(() => {
  if (getCoupon.value?.reductionMethodCode === 'RP') return `-${reductionValue.value}% Rabatt`;
  if (['RS', 'RT'].includes(getCoupon.value?.reductionMethodCode || ''))
    return `abzüglich ${reductionValue.value} €`;
  return '';
});

const customCard = computed<CardComponentProps | undefined>(() => {
  const cardProps: CardComponentProps = {};

  const squareBadge: SquareBadgeComponentProps[] = [];
  productOfferBadges.value.forEach((entry) => {
    if (!entry?.listText || squareBadge.length > 1) return;
    squareBadge.push({
      text: entry.listText,
      theme: entry?.badge?.theme ?? 'teal',
    });
  });

  if (squareBadge.length === 0) {
    if (discount.value) {
      squareBadge.push({
        text: '%',
        theme: 'teal',
      });
    } else if (hasHighlightFlag.value) {
      squareBadge.push({
        text: 'Bestseller',
        theme: 'teal',
      });
    }
  }

  // "highlightFlag"
  if (hasTreueshopFlag.value && treueShopFlagText.value) {
    squareBadge.push({
      text: treueShopFlagText.value,
      theme: 'magenta',
    });
  } else if (hasZeroPercentFinancing.value) {
    squareBadge.push({
      text: '0% Finanzierung',
      theme: 'magenta',
      clickBadge: () => {
        openModal({
          closeOnBackdrop: true,
          component: {
            is: defineAsyncComponent(() => import('~/components/zero-percent-modal-content.vue')),
          },
        });
      },
    });
  }

  if (squareBadge.length > 0) {
    cardProps.squareBadge = squareBadge;
    cardProps.squareBadgeReverse = squareBadge.length === 0;
  }
  merge(cardProps, cloneDeep(props.card));
  return cardProps;
});

const toggleBookmarkNumber = () => {
  if (props.offer?.product?.articleNumber && props.offer?.product?.name) {
    toggleBookmark(props.offer.product.articleNumber, props.offer.product.name, price.value);
  }
};

const clickContractLink = () => {
  tealiumLink({
    wt_link_id: `content.button.zu-den-details.${props.offer?.product?.articleNumber}`,
  });
  window.open(telekomContractLink.value, '_blank');
};

defineExpose({
  price,
  wasPrice,
  offerBadges,
  customCard,
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.offer-tile {
  --max-list-width: 90vw;

  &__button {
    position: relative;
  }
  &__bookmark,
  &__add-to-cart {
    //position: relative;
    //margin-top: -1rem;
  }

  &__bookmark {
    &-icon {
      fill: none;
      stroke: currentColor;
      stroke-width: 1px;
    }

    &--active {
      color: var(--color-primary);
      border-color: var(--color-primary);

      .offer-tile__bookmark-icon {
        fill: currentColor;
      }
    }
  }

  &__delivery,
  &__unavailable {
    flex: 1;
  }

  &__unavailable {
    color: var(--color-error);
  }

  &__contract-link {
    text-align: left;
    @include font-size(16px, 18px);
    white-space: normal;
    padding-block: 3px;
  }

  &__logo-image {
    height: 100%;
    align-self: flex-start;
    object-fit: contain;
    display: inline-block;
  }

  &__brand-placeholder {
    @include font-size(15px, 18px);
  }

  &__image-404 {
    display: inline-block;
    aspect-ratio: 1;
    object-fit: contain;
    background: linear-gradient(135deg, #ececec 0%, #ffffff 100%);
    height: var(--image-height);
  }

  &__svg-icon-wrapper {
    svg {
      height: 2 * $grid-base;
      width: auto;
    }
  }

  :deep(.offer-tile__icons) {
    justify-content: flex-start;
    align-self: flex-end;
    gap: 0.5 * $grid-base;
    height: 2 * $grid-base;
  }
}
</style>
